import { SerializedSocialPost, SocialPlatform } from './types';

type PostNumbers = Pick<SerializedSocialPost, 'likes' | 'comments' | 'shares' | 'saves'>;

/** Adds the numbers that exist. Null when no number exists: a sum of nothing is not 0. */
export function sumKnown(values: Array<number | null>): number | null {
  const known = values.filter((value): value is number => value !== null);
  return known.length === 0 ? null : known.reduce((total, value) => total + value, 0);
}

/** Engagement: likes + comments + shares + saves, with the numbers that the platform gives. */
export function postEngagement(post: PostNumbers): number | null {
  return sumKnown([post.likes, post.comments, post.shares, post.saves]);
}

/**
 * The address of a post on its platform, or null. The value comes from a platform, so only an
 * http or https address becomes a link. Artist Home and Performance use this one check.
 */
export function postPermalink(post: Pick<SerializedSocialPost, 'permalink'>): string | null {
  if (post.permalink === null) {
    return null;
  }
  return /^https?:\/\//i.test(post.permalink) ? post.permalink : null;
}

export interface ChannelPostTotals {
  views: number | null;
  engagement: number | null;
  /** The link clicks of the posts. Only X gives them; null on the other platforms. */
  linkClicks: number | null;
}

/** The views and the engagement of one channel, added over its posts in the list. */
export function channelPostTotals(posts: SerializedSocialPost[], platform: SocialPlatform): ChannelPostTotals {
  const own = posts.filter((post) => post.platform === platform);
  return {
    views: sumKnown(own.map((post) => post.views)),
    engagement: sumKnown(own.map(postEngagement)),
    linkClicks: sumKnown(own.map((post) => post.linkClicks)),
  };
}
